import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { DRXError, toDRXError } from '@/lib/drx-error';

/**
 * Storefront / curriculum seller profile documents (`sellers/{userId}`).
 */
/**
 * A seller's street address and ZIP stay private (`sellerPrivate/{userId}`,
 * owner and admins only); `sellers/{userId}` is public, so only city, state
 * and country go there. The full address sat on the public record, readable
 * by anyone.
 */
function splitAddress(data: Record<string, unknown>) {
  if (!('address' in data)) return { publicData: data, privateAddress: undefined };
  const a = (data.address ?? null) as Record<string, unknown> | null;
  const pick = (k: string) => (a && typeof a[k] === 'string' ? (a[k] as string) : '');
  const publicAddress = a && (pick('city') || pick('state') || pick('country'))
    ? { city: pick('city'), state: pick('state'), country: pick('country') }
    : null;
  const privateAddress = a ? { street: pick('street'), line2: pick('line2'), zipCode: pick('zipCode') } : null;
  return { publicData: { ...data, address: publicAddress }, privateAddress };
}

export class SellerRepository {
  private readonly sellersCollection = 'sellers';
  private readonly privateCollection = 'sellerPrivate';

  private async savePrivateAddress(userId: string, privateAddress: Record<string, string> | null | undefined) {
    if (privateAddress === undefined) return;
    await setDoc(doc(db, this.privateCollection, userId), { address: privateAddress, updatedAt: serverTimestamp() }, { merge: true });
  }

  private toError(error: unknown, code: string, devMessage: string) {
    return toDRXError(error, { code, devMessage });
  }

  async getSellerProfile(userId: string): Promise<Record<string, unknown> & { id: string } | null> {
    try {
      const snap = await getDoc(doc(db, this.sellersCollection, userId));
      if (!snap.exists()) return null;
      const profile = { id: snap.id, ...snap.data() } as Record<string, unknown> & { id: string };
      // The seller's own view includes the private part of the address.
      if (auth.currentUser?.uid === userId) {
        const priv = await getDoc(doc(db, this.privateCollection, userId)).catch(() => null);
        const privateAddress = priv?.exists() ? (priv.data().address as Record<string, string> | null) : null;
        if (privateAddress) profile.address = { ...((profile.address as object) ?? {}), ...privateAddress };
      }
      return profile;
    } catch (error) {
      throw this.toError(error, 'SELLER_PROFILE_FETCH_FAILED', `Failed to load seller profile for ${userId}`);
    }
  }

  /** Full document write (store onboarding completion). */
  async setSellerProfile(userId: string, data: Record<string, unknown>): Promise<void> {
    try {
      const { publicData, privateAddress } = splitAddress(data);
      await setDoc(doc(db, this.sellersCollection, userId), {
        ...publicData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await this.savePrivateAddress(userId, privateAddress);
    } catch (error) {
      throw this.toError(error, 'SELLER_PROFILE_SET_FAILED', `Failed to save seller profile for ${userId}`);
    }
  }

  async mergeSellerProfile(userId: string, data: Record<string, unknown>): Promise<void> {
    try {
      const { publicData, privateAddress } = splitAddress(data);
      await setDoc(
        doc(db, this.sellersCollection, userId),
        {
          ...publicData,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      await this.savePrivateAddress(userId, privateAddress);
    } catch (error) {
      throw this.toError(error, 'SELLER_PROFILE_MERGE_FAILED', `Failed to merge seller profile for ${userId}`);
    }
  }

  async updateSellerProfile(userId: string, patch: Record<string, unknown>): Promise<void> {
    try {
      const { publicData, privateAddress } = splitAddress(patch);
      await updateDoc(doc(db, this.sellersCollection, userId), {
        ...publicData,
        updatedAt: serverTimestamp(),
      });
      await this.savePrivateAddress(userId, privateAddress);
    } catch (error) {
      throw this.toError(error, 'SELLER_PROFILE_UPDATE_FAILED', `Failed to update seller profile for ${userId}`);
    }
  }

  /** Default analytics doc for `sellers/{userId}/sellerData/stats`. */
  async initializeSellerStats(userId: string): Promise<void> {
    try {
      await setDoc(doc(db, 'sellers', userId, 'sellerData', 'stats'), {
        totalSales: 0,
        totalRevenue: 0,
        totalDownloads: 0,
        totalResources: 0,
        storeViews: 0,
        avgRating: 0,
        lastUpdated: serverTimestamp(),
      });
    } catch (error) {
      throw new DRXError({
        code: 'SELLER_STATS_INIT_FAILED',
        devMessage: `Failed to initialize seller stats for ${userId}`,
        cause: error,
      });
    }
  }
}
